import { useEffect, useState, useCallback, useMemo, memo, useRef, startTransition } from 'react';
import { fetchData } from '../../../apiService';
// Material UI imports
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, TextField, Grid, Typography, Box, IconButton,
  FormControl, InputLabel, Select, MenuItem, Divider,
  useMediaQuery, useTheme, FormHelperText, Snackbar, Alert,
  CircularProgress, Backdrop, Autocomplete, Checkbox, Chip, Collapse, InputAdornment,
  Pagination
} from '@mui/material';
import { Close, Add, Delete, Inventory2, Visibility, CheckCircle, Warning, Search, List, ExpandMore, ExpandLess, Refresh } from '@mui/icons-material';

// PROMAX PERFORMANCE: Web Worker for ultra-fast calculations
const createCalculationWorker = () => {
  const workerCode = `
    self.onmessage = function(e) {
      const { details } = e.data;

      // Helper function to round to 2 decimal places
      const roundTo2 = (num) => Math.round((num + Number.EPSILON) * 100) / 100;

      // Ultra-fast calculation using optimized loops
      let totalQuantity = 0, totalAmount = 0, 
          totalTaxableAmount = 0, totalCGSTAmount = 0, totalSGSTAmount = 0, 
          totalIGSTAmount = 0, totalWithGST = 0;

      // Single loop optimization for maximum performance
      for (let i = 0; i < details.length; i++) {
        const d = details[i];
        const qty = parseFloat(d.Quantity) || 0;
        const amt = parseFloat(d.Amount) || 0;
        const taxableAmt = parseFloat(d.TaxableAmount) || 0;
        const cgstAmt = parseFloat(d.CGSTAmount) || 0;
        const sgstAmt = parseFloat(d.SGSTAmount) || 0;
        const igstAmt = parseFloat(d.IGSTAmount) || 0;
        const totalAmt = parseFloat(d.TotalAmount) || 0;

        totalQuantity += qty;
        totalAmount += amt;
        totalTaxableAmount += taxableAmt;
        totalCGSTAmount += cgstAmt;
        totalSGSTAmount += sgstAmt;
        totalIGSTAmount += igstAmt;
        totalWithGST += totalAmt;
      }

      // Calculate RoundOff and GrandTotal
      const calculatedTotal = roundTo2(totalWithGST);
      const roundedTotal = Math.round(calculatedTotal); // Round to nearest whole number
      const roundOff = roundTo2(roundedTotal - calculatedTotal);

      self.postMessage({
        TotalQuantity: roundTo2(totalQuantity),
        TotalAmount: roundTo2(totalAmount),
        TotalTaxableAmount: roundTo2(totalTaxableAmount),
        TotalCGSTAmount: roundTo2(totalCGSTAmount),
        TotalSGSTAmount: roundTo2(totalSGSTAmount),
        TotalIGSTAmount: roundTo2(totalIGSTAmount),
        TotalWithGST: calculatedTotal,
        RoundOff: roundOff,
        GrandTotal: roundedTotal
      });
    };
  `;

  const blob = new Blob([workerCode], { type: 'application/javascript' });
  return new Worker(URL.createObjectURL(blob));
};

const StockTransferOutModal = ({
  open,
  onClose,
  modalAction,
  formData,
  setFormData,
  transferDetails,
  setTransferDetails,
  originalTransferDetails,
  onSave,
  items,
  branches,
  loading,
  companyId,
  branchId,
  token,
  onAccept,
  onReject
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // State declarations
  const [itemsModalOpen, setItemsModalOpen] = useState(false);
  const [getItemsModalOpen, setGetItemsModalOpen] = useState(false);
  const [getItemsLoading, setGetItemsLoading] = useState(false);
  const [addingItemsLoading, setAddingItemsLoading] = useState(false);
  const [selectedItems, setSelectedItems] = useState([]);
  const [getItemsSearchValue, setGetItemsSearchValue] = useState('');
  const [itemsPerPage] = useState(50);
  const [currentItemsPage, setCurrentItemsPage] = useState(1);
  const [itemSearchValue, setItemSearchValue] = useState('');
  const [transporterLoading, setTransporterLoading] = useState(false);

  // Store original transfer details for edit mode balance calculations
  // const [originalTransferDetails, setOriginalTransferDetails] = useState([]);
  // Validation state
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});

  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'error' });

  // Cancel confirmation modal state
  const [cancelConfirmOpen, setCancelConfirmOpen] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  const isViewMode = modalAction === 'view';
  const title = modalAction === 'create' ? 'Create Stock Transfer Out' : modalAction === 'edit' ? 'Edit Stock Transfer Out' : 'View Stock Transfer Out';

  // PROMAX PERFORMANCE: Web Worker for calculations
  const calculationWorkerRef = useRef(null);
  const searchTimeoutRef = useRef(null);
  const calculationTimeoutRef = useRef(null);
  const transferDetailsRef = useRef(transferDetails);
  const formDataRef = useRef(formData);

  // Initialize Web Worker
  const isMountedRef = useRef(true);
  useEffect(() => {
    isMountedRef.current = true;

    if (!calculationWorkerRef.current) {
      calculationWorkerRef.current = createCalculationWorker();

      calculationWorkerRef.current.onmessage = (e) => {
        if (isMountedRef.current) {
          const calculatedTotals = e.data;
          startTransition(() => {
            setFormData(prev => ({ ...prev, ...calculatedTotals }));
          });
        }
      };
    }

    return () => {
      isMountedRef.current = false;
      if (calculationWorkerRef.current) {
        calculationWorkerRef.current.terminate();
        calculationWorkerRef.current = null;
      }
    };
  }, []);

  // Update refs when props change
  useEffect(() => {
    transferDetailsRef.current = transferDetails;
    formDataRef.current = formData;
  }, [transferDetails, formData]);

  // PROMAX PERFORMANCE: Web Worker-based calculations with zero main thread blocking
  const performCalculations = useCallback((details) => {
    if (!calculationWorkerRef.current || details.length === 0) {
      return;
    }

    // Send to Web Worker for non-blocking calculation
    calculationWorkerRef.current.postMessage({
      details
    });
  }, []);

  // Memoized set of already added item IDs for better performance
  const addedItemIds = useMemo(() => {
    if (!transferDetails || transferDetails.length === 0) return new Set();

    return new Set(transferDetails.map(detail => {
      const itemId = detail.ItemId || detail.Id;
      return parseInt(itemId);
    }).filter(id => !isNaN(id)));
  }, [transferDetails.length, transferDetails.map(d => d.ItemId || d.Id).join(',')]);

  // Check if item is already added to transfer (optimized with Set lookup)
  const isItemAlreadyInTransfer = useCallback((itemId) => {
    return addedItemIds.has(parseInt(itemId));
  }, [addedItemIds]);

  // Memoized items map for better performance
  const itemsMap = useMemo(() => {
    const map = new Map();
    items.forEach(item => {
      map.set(item.Id, item);
    });
    return map;
  }, [items]);

  // Fetch branch details for auto-populating transporter information
  const fetchBranchDetails = async () => {
    try {
      const response = await fetchData('BranchApi', `/BranchDetails/${branchId}`, token);
      if (response.responseCode == '1111') {
        const branchData = response.response_data || {};
        return {
          branchName: branchData.Name || '',
          location: branchData.Location || '',
          addressLine1: branchData.AddressLine1 || '',
          addressLine2: branchData.AddressLine2 || '',
          pincode: branchData.PinCode || '',
          companyName: branchData.CompanyName || branchData.Name || '',
          gstNumber: branchData.GSTNumber || branchData.CompanyGSTNumber || ''
        };
      }
      return { branchName: '', location: '', addressLine1: '', addressLine2: '', pincode: '', companyName: '', gstNumber: '' };
    } catch (error) {
      console.error('Error fetching Branch Details:', error);
      return { branchName: '', location: '', addressLine1: '', addressLine2: '', pincode: '', companyName: '', gstNumber: '' };
    }
  };

  // Handle Get Transporter from API button click
  const handleGetTransporterFromAPI = async () => {
    if (!branchId || !token) {
      showError('Branch ID or token is missing');
      return;
    }

    setTransporterLoading(true);
    try {
      const branchDetails = await fetchBranchDetails();

      // Update transporter details with company information from API
      setFormData(prev => ({
        ...prev,
        TransporterName: branchDetails.companyName,
        TransporterGST: branchDetails.gstNumber
      }));

      // Mark as having unsaved changes
      setHasUnsavedChanges(true);

      // Show success message
      setSnackbar({ 
        open: true, 
        message: 'Transporter details updated from API successfully!', 
        severity: 'success' 
      });
    } catch (error) {
      console.error('Error getting transporter details from API:', error);
      showError('Failed to get transporter details from API. Please try again.');
    } finally {
      setTransporterLoading(false);
    }
  };

  // Auto-populate transporter details when modal opens (for create mode)
  useEffect(() => {
    if (open && modalAction === 'create' && branchId && token) {
      const populateTransporterDetails = async () => {
        try {
          const branchDetails = await fetchBranchDetails();

          // Auto-populate transporter details with company information
          setFormData(prev => ({
            ...prev,
            TransporterName: branchDetails.companyName,
            TransporterGST: branchDetails.gstNumber
          }));
        } catch (error) {
          console.error('Error auto-populating transporter details:', error);
        }
      };

      populateTransporterDetails();
    }
  }, [open, modalAction, branchId, token]);

  // Style helpers
  const disabledFieldStyle = {
    '& .MuiInputBase-input.Mui-disabled': { color: '#111827', fontWeight: 600, WebkitTextFillColor: '#111827' },
    '& .MuiInputLabel-root.Mui-disabled': { color: '#9ca3af', WebkitTextFillColor: '#9ca3af' },
    '& .MuiOutlinedInput-notchedOutline': { borderColor: '#e5e7eb' }
  };

  const tinyInput = {
    '& .MuiInputBase-input': { py: 0.5, px: 0.5, fontSize: '0.8rem', textAlign: 'right' },
    '& .MuiOutlinedInput-notchedOutline': { borderColor: '#e5e7eb' }
  };

  const tinyInputCenter = {
    '& .MuiInputBase-input': { py: 0.5, px: 0.5, fontSize: '0.8rem', textAlign: 'center' },
    '& .MuiOutlinedInput-notchedOutline': { borderColor: '#e5e7eb' }
  };

  // Handle item selection in Get Items modal
  const handleItemSelection = (item, isSelected) => {
    if (isSelected) {
      const newSelectedItem = {
        ...item,
        selectedQuantity: 1,
        selectedRate: item.PurchaseRate || item.SaleRate || item.Rate || 0
      };
      setSelectedItems(prev => [...prev, newSelectedItem]);
    } else {
      setSelectedItems(prev => prev.filter(si => si.Id !== item.Id));
    }
  };

  // Handle quantity/rate change for selected items
  const handleSelectedItemChange = (itemId, field, value) => {
    // No hard block — just update, qty warning shown via cell color
    setSelectedItems(prev => prev.map(item => 
      item.Id === itemId ? { ...item, [field]: value } : item
    ));
  };

  // Remove selected item
  const removeSelectedItem = (itemId) => {
    setSelectedItems(prev => prev.filter(item => item.Id !== itemId));
  };

  // Add selected items to transfer details
  const handleAddSelectedItems = useCallback(async () => {
    if (selectedItems.length === 0) return;

    setAddingItemsLoading(true);

    try {
      const roundTo2 = (num) => Math.round((num + Number.EPSILON) * 100) / 100;

      const newItems = selectedItems.map(selectedItem => {
        const quantity = parseFloat(selectedItem.selectedQuantity) || 0;
        const rate = parseFloat(selectedItem.selectedRate) || parseFloat(selectedItem.PurchaseRate) || parseFloat(selectedItem.SaleRate) || parseFloat(selectedItem.Rate) || 0;
        const gstRate = parseFloat(selectedItem.GSTRate) || 0;

        const amount = roundTo2(quantity * rate);
        const gstAmount = roundTo2((amount * gstRate) / 100);
        const cgstAmount = roundTo2(gstAmount / 2);
        const sgstAmount = roundTo2(gstAmount / 2);
        const igstAmount = roundTo2(gstAmount);
        const totalAmount = roundTo2(amount + gstAmount);

        return {
          Id: 0,
          ItemId: selectedItem.Id,
          HSNCode: selectedItem.HSNCode || '',
          Quantity: quantity,
          Rate: rate,
          Amount: amount,
          GSTRate: gstRate,
          TaxableAmount: amount,
          CGSTAmount: cgstAmount,
          SGSTAmount: sgstAmount,
          IGSTAmount: igstAmount,
          TotalAmount: totalAmount,
          IsActive: true
        };
      });

      const updatedDetails = [...transferDetails, ...newItems];
      setTransferDetails(updatedDetails);
      performCalculations(updatedDetails);

      // Clear selections and close modal
      setSelectedItems([]);
      setGetItemsModalOpen(false);
      setHasUnsavedChanges(true);

    } catch (error) {
      console.error('Error adding items:', error);
      showError('Failed to add selected items. Please try again.');
    } finally {
      setAddingItemsLoading(false);
    }
  }, [selectedItems, transferDetails, performCalculations]);

  // Open Get Items modal
  const handleOpenGetItemsModal = () => {
    setGetItemsModalOpen(true);
    setSelectedItems([]);
    setGetItemsSearchValue('');
    setCurrentItemsPage(1);
  };

  // Close Get Items modal
  const handleCloseGetItemsModal = () => {
    setGetItemsModalOpen(false);
    setSelectedItems([]);
    setGetItemsSearchValue('');
  };

  // Filter items based on search
  const filteredItems = useMemo(() => {
    if (!getItemsSearchValue.trim()) return items;

    const searchLower = getItemsSearchValue.toLowerCase();
    return items.filter(item =>
      item.Name?.toLowerCase().includes(searchLower) ||
      item.Code?.toLowerCase().includes(searchLower) ||
      item.HSNCode?.toLowerCase().includes(searchLower) ||
      item.Description?.toLowerCase().includes(searchLower)
    );
  }, [items, getItemsSearchValue]);

  // Paginated items for Get Items modal
  const getPaginatedItems = useMemo(() => {
    const startIndex = (currentItemsPage - 1) * itemsPerPage;
    return filteredItems.slice(startIndex, startIndex + itemsPerPage);
  }, [filteredItems, currentItemsPage, itemsPerPage]);

  const totalItemsPages = Math.ceil(filteredItems.length / itemsPerPage);

  // Reset errors when modal opens/closes
  useEffect(() => {
    if (open) {
      setErrors({});
      setTouched({});
      setSnackbar({ open: false, message: '', severity: 'error' });
      setHasUnsavedChanges(false);
      setCancelConfirmOpen(false);
    }
  }, [open, modalAction]);

  // Helper function to get available balance for an item (considering edit mode)
  const getAvailableBalance = useCallback((itemId, currentDetailIndex = null) => {
    const item = itemsMap.get(parseInt(itemId));
    if (!item) return 0;

    const currentBalance = item.BalanceStock || item.CurrentBranchStock || item.Stock || 0;

    // In edit mode, add back the original allocated quantity for this item
    if (modalAction === 'edit' && originalTransferDetails.length > 0) {
      const originalDetail = originalTransferDetails.find(d => parseInt(d.ItemId) === parseInt(itemId));
      const originalQuantity = originalDetail ? (parseFloat(originalDetail.Quantity) || 0) : 0;

      // If we're checking for a specific detail row, don't add back its own original quantity
      // This prevents double-counting when validating the same row
      if (currentDetailIndex !== null) {
        const currentDetail = transferDetails[currentDetailIndex];
        if (currentDetail && parseInt(currentDetail.ItemId) === parseInt(itemId)) {
          return currentBalance + originalQuantity;
        }
      }

      return currentBalance + originalQuantity;
    }

    return currentBalance;
  }, [itemsMap, modalAction, originalTransferDetails, transferDetails]);

  // Format currency helper
  const formatCurrency = useCallback((amount) => {
    return `₹${(amount || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  }, []);

  const showError = (message) => {
    setSnackbar({ open: true, message, severity: 'error' });
  };

  const handleCloseSnackbar = () => {
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  // Handle cancel with confirmation
  const handleCancelClick = () => {
    if (hasUnsavedChanges && !isViewMode) {
      setCancelConfirmOpen(true);
    } else {
      onClose();
    }
  };

  const handleConfirmCancel = () => {
    setCancelConfirmOpen(false);
    setHasUnsavedChanges(false);
    onClose();
  };

  const handleStayOnPage = () => {
    setCancelConfirmOpen(false);
  };

  // Validation rules
  const validateField = (field, value) => {
    switch (field) {
      case 'StockTransferNumber':
        if (!value || value.toString().trim() === '') return 'Transfer Number is required';
        return '';
      case 'StockTransferDate':
        if (!value) return 'Transfer Date is required';
        return '';
      case 'ToBranchId':
        if (!value) return 'To Branch is required';
        return '';
      default:
        return '';
    }
  };

  // Validate all fields
  const validateForm = () => {
    const newErrors = {};

    // Required field validations
    const StockTransferNumberError = validateField('StockTransferNumber', formData.StockTransferNumber);
    if (StockTransferNumberError) newErrors.StockTransferNumber = StockTransferNumberError;

    const StockTransferDateError = validateField('StockTransferDate', formData.StockTransferDate);
    if (StockTransferDateError) newErrors.StockTransferDate = StockTransferDateError;

    const toBranchError = validateField('ToBranchId', formData.ToBranchId);
    if (toBranchError) newErrors.ToBranchId = toBranchError;

    // Transfer details validation
    if (transferDetails.length === 0) {
      newErrors.transferDetails = 'At least one item is required';
    } else {
      const detailErrors = [];
      transferDetails.forEach((detail, index) => {
        const itemErrors = {};
        if (!detail.ItemId) itemErrors.ItemId = 'Item is required';
        if (!detail.Quantity || parseFloat(detail.Quantity) <= 0) {
          itemErrors.Quantity = 'Quantity must be greater than 0';
        }
        if (!detail.Rate || parseFloat(detail.Rate) <= 0) {
          itemErrors.Rate = 'Rate must be greater than 0';
        }
        if (Object.keys(itemErrors).length > 0) {
          detailErrors[index] = itemErrors;
        }
      });
      if (detailErrors.length > 0) {
        newErrors.details = detailErrors;
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle field blur for touched state
  const handleBlur = (field) => {
    setTouched(prev => ({ ...prev, [field]: true }));
    const error = validateField(field, formData[field]);
    setErrors(prev => ({ ...prev, [field]: error }));
  };

  // Handle save with validation
  const handleSaveWithValidation = () => {
    // Mark all fields as touched
    setTouched({ StockTransferNumber: true, StockTransferDate: true, ToBranchId: true });

    // Build validation error messages
    const validationMessages = [];

    if (!formData.StockTransferNumber || formData.StockTransferNumber.toString().trim() === '') {
      validationMessages.push('Transfer Number is required');
    }
    if (!formData.StockTransferDate) {
      validationMessages.push('Transfer Date is required');
    }
    if (!formData.ToBranchId) {
      validationMessages.push('To Branch is required');
    }
    if (transferDetails.length === 0) {
      validationMessages.push('At least one item is required');
    } else {
      // Check each item for Quantity and Rate
      const itemErrors = [];
      transferDetails.forEach((detail, index) => {
        if (!detail.ItemId) {
          itemErrors.push(`Row ${index + 1}: Item is required`);
        }
        if (!detail.Quantity || parseFloat(detail.Quantity) <= 0) {
          itemErrors.push(`Row ${index + 1}: Quantity is required`);
        }
        if (!detail.Rate || parseFloat(detail.Rate) <= 0) {
          itemErrors.push(`Row ${index + 1}: Rate is required`);
        }
      });
      if (itemErrors.length > 0) {
        validationMessages.push(...itemErrors);
      }
    }

    if (validationMessages.length > 0) {
      showError(validationMessages.join(' | '));
      validateForm();
      return;
    }

    if (validateForm()) {
      onSave();
    }
  };

  const handleChange = (field, value) => {
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }

    // Mark as having unsaved changes
    if (!isViewMode) {
      setHasUnsavedChanges(true);
    }

    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleDetailChange = (index, field, value) => {
    // Mark as having unsaved changes
    if (!isViewMode) {
      setHasUnsavedChanges(true);
    }

    const updatedDetails = [...transferDetails];

    // Handle ItemId change - auto-update HSN code and rate
    if (field === 'ItemId') {
      const selectedItem = itemsMap.get(parseInt(value));
      if (selectedItem) {
        updatedDetails[index] = { 
          ...updatedDetails[index], 
          ItemId: value,
          HSNCode: selectedItem.HSNCode || '',
          GSTRate: selectedItem.GSTRate || 0,
          Rate: selectedItem.PurchaseRate || selectedItem.SaleRate || selectedItem.Rate || 0
        };
      } else {
        updatedDetails[index] = { ...updatedDetails[index], [field]: value };
      }
    } else if (['Quantity', 'Rate', 'GSTRate'].includes(field)) {
      if (value === '' || value === null || value === undefined) {
        // Keep empty string for display but still recalculate amounts with 0
        updatedDetails[index] = { ...updatedDetails[index], [field]: '' };
        // Fall through to recalculation below (don't return early)
      } else {
        const validNumberRegex = /^\d*\.?\d*$/;
        if (!validNumberRegex.test(value)) return;
        const numValue = parseFloat(value);
        if (isNaN(numValue) || numValue < 0) return;
        updatedDetails[index] = { ...updatedDetails[index], [field]: value };
      }
    } else {
      updatedDetails[index] = { ...updatedDetails[index], [field]: value };
    }

    if (['Quantity', 'Rate', 'GSTRate', 'ItemId'].includes(field)) {
      const currentDetail = updatedDetails[index];
      const quantity = Math.max(0, parseFloat(currentDetail.Quantity) || 0);
      const rate = Math.max(0, parseFloat(currentDetail.Rate) || 0);
      const gstRate = Math.max(0, parseFloat(currentDetail.GSTRate) || 0);

      // Helper function to round to 2 decimal places
      const roundTo2 = (num) => Math.round((num + Number.EPSILON) * 100) / 100;

      // Calculate basic amount
      const amount = roundTo2(quantity * rate);

      // Calculate taxable amount (no discount)
      const taxableAmount = amount;

      // Calculate GST amounts
      const gstAmount = roundTo2((taxableAmount * gstRate) / 100);
      const cgstAmount = roundTo2(gstAmount / 2);
      const sgstAmount = roundTo2(gstAmount / 2);
      const igstAmount = roundTo2(gstAmount); // For IGST scenarios

      // Total amount including GST
      const totalAmount = roundTo2(taxableAmount + gstAmount);

      updatedDetails[index] = {
        ...updatedDetails[index],
        Amount: amount,
        TaxableAmount: taxableAmount,
        CGSTAmount: cgstAmount,
        SGSTAmount: sgstAmount,
        IGSTAmount: igstAmount,
        TotalAmount: totalAmount
      };
    }
    setTransferDetails(updatedDetails);
    performCalculations(updatedDetails);
  };

  const removeDetailRow = (index) => {
    setHasUnsavedChanges(true);
    const updatedDetails = transferDetails.filter((_, i) => i !== index);
    setTransferDetails(updatedDetails);
    performCalculations(updatedDetails);
  };

  useEffect(() => {
    // Auto-calculate totals when details change (length or any quantity/amount value)
    if (!isViewMode) {
      if (transferDetails.length > 0) {
        performCalculations(transferDetails);
      } else {
        // All rows removed — reset totals to zero
        setFormData(prev => ({
          ...prev,
          TotalQuantity: 0, TotalAmount: 0,
          TotalTaxableAmount: 0, TotalCGSTAmount: 0,
          TotalSGSTAmount: 0, TotalIGSTAmount: 0,
          TotalWithGST: 0, RoundOff: 0, GrandTotal: 0
        }));
      }
    }
  }, [
    transferDetails.length,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    transferDetails.map(d => `${d.Quantity}|${d.Rate}|${d.GSTRate}`).join(','),
    performCalculations
  ]);

  // Handle View All Items modal opening
  const handleViewAllItemsOpen = () => {
    setItemsModalOpen(true);
  };

  const handleViewAllItemsClose = () => {
    setItemsModalOpen(false);
  };

  const compactInput = {
    '& .MuiInputBase-input': { py: 0.3, fontSize: '0.78rem', color: '#111827' },
    '& .MuiInputLabel-root': { fontSize: '0.72rem', color: '#9ca3af' },
    '& .MuiInputLabel-root.Mui-focused': { color: '#6b7280' },
    '& .MuiInputLabel-root.Mui-disabled': { color: '#9ca3af' },
    '& .MuiOutlinedInput-root': { borderRadius: '6px' },
    ...(isViewMode && disabledFieldStyle)
  };

  return (
    <Dialog open={open} onClose={(e, reason) => {
      if (reason === 'backdropClick' || reason === 'escapeKeyDown') {
        if (hasUnsavedChanges && !isViewMode) {
          setCancelConfirmOpen(true);
          return;
        }
      }
      onClose();
    }}
      maxWidth="xl" fullWidth fullScreen={isMobile} disableEscapeKeyDown
      PaperProps={{ sx: { borderRadius: isMobile ? 0 : '10px', m: isMobile ? 0 : 2, maxWidth: '1400px', position: 'relative' } }}>

      <DialogTitle sx={{ py: 1, px: 2, borderBottom: isViewMode ? '1px solid #e2e8f0' : '1px solid #dbeafe', bgcolor: isViewMode ? '#1e293b' : '#eff6ff' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Inventory2 sx={{ color: isViewMode ? '#f8fafc' : '#2563eb', fontSize: 20 }} />
            <Typography variant="subtitle1" sx={{ fontWeight: 600, color: isViewMode ? '#f8fafc' : '#1e3a8a', fontSize: isMobile ? '0.9rem' : '1rem' }}>{title}</Typography>
          </Box>
          <IconButton onClick={handleCancelClick} size="small" sx={{ p: 0.5, color: isViewMode ? '#f8fafc' : 'inherit' }}><Close fontSize="small" /></IconButton>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ p: isMobile ? 1.5 : 2, pt: isMobile ? 1.5 : 1.5, bgcolor: isViewMode ? '#f8fafc' : '#fefffe' }}>

        {/* VIEW MODE: same layout as edit, fields disabled */}
        <Box sx={{ bgcolor: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 1, p: 1, mb: 1, mt: 1 }}>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'nowrap', mb: 1 }}>
            {/* <Box sx={{ flex: '0 0 130px' }}>
              <TextField fullWidth size="small" label="Transfer No *"
                value={formData.StockTransferPrefix && formData.FinYear
                  ? `${formData.StockTransferPrefix}/${formData.StockTransferNumber}/${formData.FinYear}`
                  : formData.StockTransferNumber || ''}
                onChange={(e) => handleChange('StockTransferNumber', e.target.value)}
                onBlur={() => handleBlur('StockTransferNumber')}
                disabled={isViewMode}
                error={!isViewMode && touched.StockTransferNumber && !!errors.StockTransferNumber}
                helperText={!isViewMode && touched.StockTransferNumber && errors.StockTransferNumber}
                sx={{ ...compactInput, ...(isViewMode && disabledFieldStyle) }} />
            </Box> */}
            <Box sx={{ flex: '0 0 130px' }}>
              <TextField fullWidth size="small" label="Transfer No *"
                value={formData.StockTransferPrefix && formData.FinYear
                  ? `${formData.StockTransferPrefix}/${formData.StockTransferNumber}/${formData.FinYear}`
                  : formData.StockTransferNumber || ''}
                InputProps={{ readOnly: true }}
                disabled={isViewMode}
                sx={{ ...compactInput, ...(isViewMode && disabledFieldStyle), '& .MuiInputBase-root': { bgcolor: '#f0f9ff' } }} />
            </Box>
            <Box sx={{ flex: '0 0 130px' }}>
              <TextField fullWidth size="small" type="date" label="Transfer Date *"
                value={formData.StockTransferDate || ''}
                onChange={(e) => handleChange('StockTransferDate', e.target.value)}
                onBlur={() => handleBlur('StockTransferDate')}
                disabled={isViewMode}
                error={!isViewMode && touched.StockTransferDate && !!errors.StockTransferDate}
                helperText={!isViewMode && touched.StockTransferDate && errors.StockTransferDate}
                InputLabelProps={{ shrink: true }} sx={{ ...compactInput, ...(isViewMode && disabledFieldStyle) }} />
            </Box>
            <Box sx={{ flex: '0 0 140px' }}>
              {isViewMode ? (
                <TextField fullWidth size="small" label="To Branch"
                  value={(() => {
                    const b = branches.find(b => b.Id === formData.ToBranchId);
                    return b ? (b.Code ? `${b.Code} - ${b.Name}` : b.Name) : '-';
                  })()}
                  disabled sx={{ ...compactInput, ...disabledFieldStyle }} />
              ) : (
                <FormControl fullWidth size="small" sx={compactInput} error={touched.ToBranchId && !!errors.ToBranchId}>
                  <InputLabel>To Branch *</InputLabel>
                  <Select value={formData.ToBranchId || ''} onChange={(e) => handleChange('ToBranchId', e.target.value)}
                    onBlur={() => handleBlur('ToBranchId')} label="To Branch *" sx={{ fontSize: '0.78rem' }}
                    MenuProps={{ PaperProps: { sx: { maxHeight: 200, '& .MuiMenuItem-root': { fontSize: '0.85rem' } } } }}>
                    {branches.filter(b => b.Id !== branchId).map(branch => (
                      <MenuItem key={branch.Id} value={branch.Id} sx={{ fontSize: '0.85rem', py: 0.5 }}>{branch.Code ? `${branch.Code} - ${branch.Name}` : branch.Name}</MenuItem>
                    ))}
                  </Select>
                  {touched.ToBranchId && errors.ToBranchId && <FormHelperText>{errors.ToBranchId}</FormHelperText>}
                </FormControl>
              )}
            </Box>
            <Box sx={{ flex: 2, minWidth: 0 }}>
              <TextField fullWidth size="small" label="Transporter Name" value={formData.TransporterName || ''}
                onChange={(e) => handleChange('TransporterName', e.target.value)}
                disabled={isViewMode} sx={{ ...compactInput, ...(isViewMode && disabledFieldStyle) }} />
            </Box>
            <Box sx={{ flex: 1.5, minWidth: 0 }}>
              <TextField fullWidth size="small" label="Transporter GST" value={formData.TransporterGST || ''}
                onChange={(e) => handleChange('TransporterGST', e.target.value)}
                disabled={isViewMode} sx={{ ...compactInput, ...(isViewMode && disabledFieldStyle) }} />
            </Box>
            <Box sx={{ flex: 1.2, minWidth: 0 }}>
              <TextField fullWidth size="small" label="Vehicle No" value={formData.VehicleNumber || ''}
                onChange={(e) => handleChange('VehicleNumber', e.target.value)}
                disabled={isViewMode} sx={{ ...compactInput, ...(isViewMode && disabledFieldStyle) }} />
            </Box>
            <Box sx={{ flex: '0 0 65px' }}>
              <TextField fullWidth size="small" type="number" label="KM" value={formData.Distance || ''}
                onChange={(e) => handleChange('Distance', e.target.value)}
                disabled={isViewMode} inputProps={{ min: 0 }} sx={{ ...compactInput, ...(isViewMode && disabledFieldStyle) }} />
            </Box>
          </Box>
          <TextField fullWidth size="small" label="Remarks" value={formData.Remarks || ''}
            onChange={(e) => handleChange('Remarks', e.target.value)}
            disabled={isViewMode} sx={{ ...compactInput, ...(isViewMode && disabledFieldStyle) }} />
        </Box>

        <Divider sx={{ my: 1 }} />

        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="caption" sx={{ fontWeight: 600, color: errors.transferDetails ? '#dc2626' : (isViewMode ? '#475569' : '#2563eb') }}>Transfer Items *</Typography>
            <Typography variant="caption" sx={{ color: '#6b7280' }}>({transferDetails.length} items)</Typography>
            {errors.transferDetails && (
              <Typography variant="caption" sx={{ color: '#dc2626', fontSize: '0.7rem' }}>({errors.transferDetails})</Typography>
            )}
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {transferDetails.length > 5 && (
              <Button
                size="small"
                onClick={handleViewAllItemsOpen}
                startIcon={<Visibility sx={{ fontSize: 14 }} />}
                sx={{ py: 0.25, px: 1, fontSize: '0.7rem', minHeight: 0, color: '#1e40af' }}
              >
                View All
              </Button>
            )}
            {!isViewMode && (
              <Button 
                size="small" 
                startIcon={<List sx={{ fontSize: 16 }} />} 
                onClick={handleOpenGetItemsModal} 
                variant="contained" 
                sx={{ 
                  py: 0.25, 
                  px: 1, 
                  fontSize: '0.7rem', 
                  minHeight: 0, 
                  bgcolor: '#1e40af', 
                  '&:hover': { bgcolor: '#1d4ed8' }
                }}
              >
                Get Items
              </Button>
            )}
          </Box>
        </Box>

        {/* Desktop Table View - Fixed height for 5 rows */}
        {!isMobile && (
          <Box sx={{ maxHeight: 200, overflow: 'auto', mb: 1.5, border: errors.transferDetails ? '1px solid #dc2626' : (isViewMode ? '1px solid #e2e8f0' : '1px solid #dbeafe'), borderRadius: 1 }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.75rem' }}>
              <thead style={{ position: 'sticky', top: 0, zIndex: 1 }}>
                <tr style={{ background: isViewMode ? 'linear-gradient(135deg, #334155 0%, #1e293b 100%)' : 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)' }}>
                  <th style={{ padding: '6px 8px', textAlign: 'center', borderBottom: '1px solid #dbeafe', width: '4%', background: 'transparent', color: 'white', fontWeight: 600 }}>Sl No</th>
                  <th style={{ padding: '6px 8px', textAlign: 'left', borderBottom: '1px solid #dbeafe', width: '22%', background: 'transparent', color: 'white', fontWeight: 600 }}>Item</th>
                  <th style={{ padding: '6px 8px', textAlign: 'center', borderBottom: '1px solid #dbeafe', width: '8%', background: 'transparent', color: 'white', fontWeight: 600 }}>HSN Code</th>
                  <th style={{ padding: '6px 8px', textAlign: 'center', borderBottom: '1px solid #dbeafe', width: '8%', background: 'transparent', color: 'white', fontWeight: 600 }}>Curr. Stock</th>
                  <th style={{ padding: '6px 8px', textAlign: 'center', borderBottom: '1px solid #dbeafe', width: '8%', background: 'transparent', color: 'white', fontWeight: 600 }}>Qty</th>
                  <th style={{ padding: '6px 8px', textAlign: 'right', borderBottom: '1px solid #dbeafe', width: '10%', background: 'transparent', color: 'white', fontWeight: 600 }}>Rate</th>
                  <th style={{ padding: '6px 8px', textAlign: 'center', borderBottom: '1px solid #dbeafe', width: '8%', background: 'transparent', color: 'white', fontWeight: 600 }}>GST%</th>
                  <th style={{ padding: '6px 8px', textAlign: 'right', borderBottom: '1px solid #dbeafe', width: '12%', background: 'transparent', color: 'white', fontWeight: 600 }}>Taxable</th>
                  <th style={{ padding: '6px 8px', textAlign: 'right', borderBottom: '1px solid #dbeafe', width: '12%', background: 'transparent', color: 'white', fontWeight: 600 }}>Total</th>
                  {!isViewMode && <th style={{ padding: '6px 8px', textAlign: 'center', borderBottom: '1px solid #dbeafe', width: '6%', background: 'transparent', color: 'white', fontWeight: 600 }}></th>}
                </tr>
              </thead>
              <tbody>
                {transferDetails.map((d, index) => (
                  <tr key={index} style={{ backgroundColor: index % 2 === 0 ? (isViewMode ? '#f1f5f9' : '#eff6ff') : '#ffffff' }}>
                    <td style={{ padding: '4px 8px', borderBottom: '1px solid #dbeafe', textAlign: 'center', fontWeight: 600, color: isViewMode ? '#475569' : '#2563eb' }}>
                      {index + 1}
                    </td>
                    <td style={{ padding: '4px 8px', borderBottom: '1px solid #dbeafe', fontWeight: 600 }}>
                      <span style={{ fontWeight: 600, color: '#1e3a8a' }}>{items.find(i => i.Id === parseInt(d.ItemId))?.Name || '-'}</span>
                    </td>
                    <td style={{ padding: '4px 8px', borderBottom: '1px solid #dbeafe', textAlign: 'center', fontWeight: 600, color: '#6b7280' }}>
                      {d.HSNCode || '-'}
                    </td>
                    <td style={{ padding: '4px 8px', borderBottom: '1px solid #dbeafe', textAlign: 'center', fontWeight: 600 }}>
                      {(() => {
                        const item = itemsMap.get(parseInt(d.ItemId));
                        const currStock = parseFloat(item?.CurrentBranchStock ?? item?.Stock ?? 0);
                        const qty = parseFloat(d.Quantity) || 0;
                        const color = currStock <= 0 ? '#dc2626' : qty > currStock ? '#f59e0b' : '#059669';
                        return (
                          <span style={{ color, fontSize: '0.75rem', fontWeight: 700 }}>
                            {currStock}
                          </span>
                        );
                      })()}
                    </td>
                    <td style={{ padding: '4px 8px', borderBottom: '1px solid #dbeafe', textAlign: 'center', fontWeight: isViewMode ? 600 : 400 }}>
                      {isViewMode ? (
                        <span style={{ fontWeight: 600, color: '#1e3a8a' }}>{d.Quantity || 0}</span>
                      ) : (
                        <TextField 
                          size="small" 
                          type="number" 
                          value={d.Quantity ?? ''} 
                          onChange={(e) => handleDetailChange(index, 'Quantity', e.target.value)} 
                          disabled={isViewMode} 
                          onFocus={(e) => e.target.select()} 
                          inputProps={{ 
                            min: 0, 
                            style: { textAlign: 'center', padding: '4px', fontSize: '0.75rem' } 
                          }} 
                          sx={{
                            width: '100%',
                            '& .MuiOutlinedInput-root': {
                              '& fieldset': {
                                borderColor: (() => {
                                  const item = itemsMap.get(parseInt(d.ItemId));
                                  const currStock = parseFloat(item?.BalanceStock ?? item?.Stock ?? 0);
                                  return parseFloat(d.Quantity || 0) > currStock ? '#f59e0b' : undefined;
                                })()
                              }
                            }
                          }}
                        />
                      )}
                    </td>
                    <td style={{ padding: '4px 8px', borderBottom: '1px solid #dbeafe', textAlign: 'right', fontWeight: isViewMode ? 600 : 400 }}>
                      {isViewMode ? (
                        <span style={{ fontWeight: 600, color: '#1e3a8a' }}>{formatCurrency(d.Rate)}</span>
                      ) : (
                        <TextField size="small" type="number" value={d.Rate ?? ''} onChange={(e) => handleDetailChange(index, 'Rate', e.target.value)} disabled={isViewMode} onFocus={(e) => e.target.select()} inputProps={{ min: 0, style: { textAlign: 'right', padding: '4px', fontSize: '0.75rem' } }} sx={{ width: '100%' }} />
                      )}
                    </td>
                    <td style={{ padding: '4px 8px', borderBottom: '1px solid #dbeafe', textAlign: 'center', fontWeight: isViewMode ? 600 : 400 }}>
                      {isViewMode ? (
                        <span style={{ fontWeight: 600, color: '#1e3a8a' }}>{d.GSTRate || 0}%</span>
                      ) : (
                        <TextField size="small" type="number" value={d.GSTRate ?? 0} onChange={(e) => handleDetailChange(index, 'GSTRate', e.target.value)} disabled={isViewMode} onFocus={(e) => e.target.select()} inputProps={{ min: 0, max: 100, style: { textAlign: 'center', padding: '4px', fontSize: '0.75rem' } }} sx={{ width: '100%' }} />
                      )}
                    </td>
                    <td style={{ padding: '4px 8px', borderBottom: '1px solid #dbeafe', textAlign: 'right', fontWeight: 600, color: '#1e3a8a' }}>{formatCurrency(d.TaxableAmount)}</td>
                    <td style={{ padding: '4px 8px', borderBottom: '1px solid #dbeafe', textAlign: 'right', fontWeight: 600, color: '#2563eb' }}>{formatCurrency(d.TotalAmount)}</td>
                    {!isViewMode && (
                      <td style={{ padding: '4px 8px', borderBottom: '1px solid #dbeafe', textAlign: 'center' }}>
                        <IconButton size="small" onClick={() => removeDetailRow(index)} sx={{ p: 0.25, color: '#dc2626' }}><Delete sx={{ fontSize: 16 }} /></IconButton>
                      </td>
                    )}
                  </tr>
                ))}
                {transferDetails.length === 0 && (
                  <tr><td colSpan={isViewMode ? 8 : 9} style={{ padding: '20px', textAlign: 'center', color: '#6b7280', backgroundColor: isViewMode ? '#f1f5f9' : '#eff6ff' }}>No items added</td></tr>
                )}
              </tbody>
            </table>
          </Box>
        )}

        {/* Mobile Card View */}
        {isMobile && (
          <Box sx={{ maxHeight: 300, overflow: 'auto', mb: 1.5 }}>
            {transferDetails.map((d, index) => (
              <Box key={index} sx={{ border: '1px solid #dbeafe', borderRadius: 2, p: 1.5, mb: 1.5, bgcolor: '#eff6ff' }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                  <Typography variant="body2" sx={{ fontWeight: 600, color: '#1e3a8a', fontSize: '0.8rem' }}>
                    {items.find(i => i.Id === parseInt(d.ItemId))?.Name || 'Select Item'}
                  </Typography>
                  {!isViewMode && (
                    <IconButton size="small" onClick={() => removeDetailRow(index)} sx={{ p: 0.5, bgcolor: '#eff6ff', color: '#dc2626' }}><Delete sx={{ fontSize: 16 }} /></IconButton>
                  )}
                </Box>
                <Box sx={{ mb: 1 }}>
                  <Typography variant="caption" sx={{ color: '#2563eb', fontSize: '0.65rem', fontWeight: 600 }}>Item</Typography>
                  <TextField 
                    fullWidth 
                    size="small" 
                    value={items.find(i => i.Id === parseInt(d.ItemId))?.Name || '-'} 
                    disabled 
                    sx={{ '& .MuiInputBase-input': { py: 0.75, fontSize: '0.8rem', fontWeight: 600, color: '#1e3a8a' } }} 
                  />
                </Box>
                <Box sx={{ mb: 1 }}>
                  <Typography variant="caption" sx={{ color: '#2563eb', fontSize: '0.65rem', fontWeight: 600 }}>HSN Code</Typography>
                  <TextField 
                    fullWidth 
                    size="small" 
                    value={d.HSNCode || '-'} 
                    disabled 
                    sx={{ '& .MuiInputBase-input': { py: 0.75, fontSize: '0.8rem', fontWeight: 600, color: '#6b7280' } }} 
                  />
                </Box>
                <Box sx={{ mb: 1 }}>
                  <Typography variant="caption" sx={{ color: '#2563eb', fontSize: '0.65rem', fontWeight: 600 }}>Balance Stock</Typography>
                  <TextField 
                    fullWidth 
                    size="small" 
                    value={(() => {
                      const item = itemsMap.get(parseInt(d.ItemId));
                      return parseFloat(item?.BalanceStock ?? item?.Stock ?? 0);
                    })()} 
                    disabled 
                    sx={{ 
                      '& .MuiInputBase-input': { 
                        py: 0.75, 
                        fontSize: '0.8rem', 
                        fontWeight: 600, 
                        textAlign: 'center',
                        color: (() => {
                          const item = itemsMap.get(parseInt(d.ItemId));
                          const currStock = parseFloat(item?.BalanceStock ?? item?.Stock ?? 0);
                          const qty = parseFloat(d.Quantity) || 0;
                          return currStock <= 0 ? '#dc2626' : qty > currStock ? '#f59e0b' : '#059669';
                        })()
                      } 
                    }} 
                  />
                </Box>
                <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="caption" sx={{ color: '#2563eb', fontSize: '0.65rem', fontWeight: 600 }}>Quantity</Typography>
                    <TextField 
                      size="small" 
                      type="number" 
                      value={d.Quantity || ''} 
                      onChange={(e) => handleDetailChange(index, 'Quantity', e.target.value)} 
                      disabled={isViewMode} 
                      fullWidth 
                      inputProps={{ min: 0 }} 
                      sx={{
                        '& .MuiInputBase-input': { py: 0.75, fontSize: '0.8rem', textAlign: 'center' },
                        '& .MuiOutlinedInput-root': {
                          '& fieldset': {
                            borderColor: (() => {
                              const item = itemsMap.get(parseInt(d.ItemId));
                              const currStock = parseFloat(item?.BalanceStock ?? item?.Stock ?? 0);
                              return parseFloat(d.Quantity || 0) > currStock ? '#f59e0b' : undefined;
                            })()
                          }
                        }
                      }}
                      helperText={(() => {
                        const item = itemsMap.get(parseInt(d.ItemId));
                        const currStock = parseFloat(item?.BalanceStock ?? item?.Stock ?? 0);
                        const qty = parseFloat(d.Quantity || 0);
                        return qty > currStock ? `Exceeds stock (${currStock})` : '';
                      })()}
                    />
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="caption" sx={{ color: '#2563eb', fontSize: '0.65rem', fontWeight: 600 }}>Rate</Typography>
                    <TextField size="small" type="number" value={d.Rate || ''} onChange={(e) => handleDetailChange(index, 'Rate', e.target.value)} disabled={isViewMode} fullWidth inputProps={{ min: 0 }} sx={{ '& .MuiInputBase-input': { py: 0.75, fontSize: '0.8rem', textAlign: 'right' } }} />
                  </Box>
                </Box>
                <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="caption" sx={{ color: '#2563eb', fontSize: '0.65rem', fontWeight: 600 }}>GST %</Typography>
                    <TextField size="small" type="number" value={d.GSTRate || 0} onChange={(e) => handleDetailChange(index, 'GSTRate', e.target.value)} disabled={isViewMode} fullWidth inputProps={{ min: 0, max: 100 }} sx={{ '& .MuiInputBase-input': { py: 0.75, fontSize: '0.8rem', textAlign: 'center' } }} />
                  </Box>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', pt: 1, borderTop: '1px solid #dbeafe', mb: 0.5 }}>
                  <Typography variant="caption" sx={{ color: '#2563eb', fontWeight: 600 }}>Taxable Amount</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 600, color: '#1e3a8a' }}>{formatCurrency(d.TaxableAmount)}</Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', pt: 1, borderTop: '1px solid #dbeafe' }}>
                  <Typography variant="caption" sx={{ color: '#2563eb', fontWeight: 600 }}>Total Amount</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 600, color: '#2563eb' }}>{formatCurrency(d.TotalAmount)}</Typography>
                </Box>
              </Box>
            ))}
            {transferDetails.length === 0 && (
              <Box sx={{ textAlign: 'center', py: 4, color: '#2563eb', bgcolor: '#eff6ff', borderRadius: 1, border: '1px dashed #dbeafe' }}><Typography variant="body2">No items added</Typography></Box>
            )}
          </Box>
        )}

        <Divider sx={{ my: 1 }} />

        {/* Summary Section */}
        <Grid container spacing={1}>
          <Grid item xs={12} sm={8}>
            {/* Audit Info */}
            {isViewMode && (
              <Box sx={{ bgcolor: '#f1f5f9', p: 1, borderRadius: 1, border: '1px solid #e2e8f0' }}>
                <Typography variant="caption" sx={{ color: '#64748b', display: 'block', fontSize: '0.72rem' }}>
                  Created by <strong style={{ color: '#334155' }}>{formData.CreatedByName || '-'}</strong>
                  {formData.CreatedDate && <> · {new Date(formData.CreatedDate).toLocaleDateString('en-IN', { day: '2-digit', month: '2-digit', year: 'numeric' })}, {new Date(formData.CreatedDate).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: true })}</>}
                </Typography>
                {formData.ModifiedByName && (
                  <Typography variant="caption" sx={{ color: '#64748b', display: 'block', fontSize: '0.72rem' }}>
                    Modified by <strong style={{ color: '#334155' }}>{formData.ModifiedByName}</strong>
                    {formData.ModifiedDate && <> · {new Date(formData.ModifiedDate).toLocaleDateString('en-IN', { day: '2-digit', month: '2-digit', year: 'numeric' })}, {new Date(formData.ModifiedDate).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: true })}</>}
                  </Typography>
                )}
              </Box>
            )}
          </Grid>

          <Grid item xs={12} sm={4}>
            <Box sx={{ bgcolor: isViewMode ? '#f1f5f9' : '#eff6ff', p: 1.5, borderRadius: 1, border: isViewMode ? '1px solid #e2e8f0' : '1px solid #dbeafe' }}>
              {[
                ['Total Quantity', formData.TotalQuantity || 0, false],
                ['Taxable Amount', formatCurrency(formData.TotalTaxableAmount), false],
                ['CGST Amount', formatCurrency(formData.TotalCGSTAmount), false],
                ['SGST Amount', formatCurrency(formData.TotalSGSTAmount), false],
                ['Total with GST', formatCurrency(formData.TotalWithGST), false],
                ['Round Off', formatCurrency(formData.RoundOff || 0), false],
              ].map(([label, val]) => (
                <Box key={label} sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.4 }}>
                  <Typography variant="caption" sx={{ color: '#64748b' }}>{label}</Typography>
                  <Typography variant="caption" sx={{ fontWeight: 600, color: '#2563eb' }}>{val}</Typography>
                </Box>
              ))}
              <Divider sx={{ my: 0.5 }} />
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography variant="body2" sx={{ fontWeight: 700, color: '#1e293b' }}>Grand Total</Typography>
                <Typography variant="body2" sx={{ fontWeight: 800, color: '#1e40af' }}>₹{(formData.GrandTotal || 0).toLocaleString('en-IN')}</Typography>
              </Box>
            </Box>
          </Grid>
        </Grid>

      </DialogContent>

      <DialogActions sx={{ px: 2, py: 1.5, borderTop: isViewMode ? '1px solid #e2e8f0' : '1px solid #dbeafe', bgcolor: isViewMode ? '#f8fafc' : '#eff6ff' }}>
        <Button onClick={handleCancelClick} variant="outlined" size="small">Cancel</Button>
        {/* {isViewMode && onAccept && (
          <Button onClick={() => onAccept(formData.Id)} variant="contained" size="small"
            sx={{ bgcolor: '#16a34a', '&:hover': { bgcolor: '#15803d' }, textTransform: 'none' }}>
            Accept
          </Button>
        )}
        {isViewMode && onReject && (
          <Button onClick={() => onReject(formData.Id)} variant="contained" size="small"
            sx={{ bgcolor: '#dc2626', '&:hover': { bgcolor: '#b91c1c' }, textTransform: 'none' }}>
            Reject
          </Button>
        )} */}
        {!isViewMode && <Button onClick={handleSaveWithValidation} variant="contained" size="small" disabled={loading}>{modalAction === 'edit' ? 'Update' : 'Save'}</Button>}
      </DialogActions>

      {/* View All Items Modal */}
      <Dialog 
        open={itemsModalOpen} 
        onClose={handleViewAllItemsClose} 
        maxWidth="lg"
        fullWidth 
        PaperProps={{ 
          sx: { 
            borderRadius: '12px', 
            maxHeight: '90vh', 
            position: 'relative', 
            m: 1
          } 
        }}
      >
        <DialogTitle sx={{ py: 1, px: 1.5, borderBottom: '1px solid #dbeafe', bgcolor: '#eff6ff' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Inventory2 sx={{ color: '#2563eb', fontSize: 18 }} />
              <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#1e3a8a', fontSize: '0.9rem' }}>All Transfer Items ({transferDetails.length})</Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <IconButton onClick={handleViewAllItemsClose} size="small" sx={{ p: 0.4 }}><Close fontSize="small" /></IconButton>
            </Box>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ p: 0 }}>
          <Box sx={{ maxHeight: '70vh', overflow: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.75rem' }}>
              <thead style={{ position: 'sticky', top: 0, zIndex: 1 }}>
                <tr style={{ background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)' }}>
                  <th style={{ padding: '8px 6px', textAlign: 'center', color: 'white', fontWeight: 600, fontSize: '0.7rem' }}>#</th>
                  <th style={{ padding: '8px 8px', textAlign: 'left', color: 'white', fontWeight: 600, fontSize: '0.7rem', minWidth: '150px' }}>Item Name</th>
                  <th style={{ padding: '8px 6px', textAlign: 'center', color: 'white', fontWeight: 600, fontSize: '0.7rem', width: '100px' }}>HSN Code</th>
                  <th style={{ padding: '8px 6px', textAlign: 'center', color: 'white', fontWeight: 600, fontSize: '0.7rem', width: '80px' }}>Curr. Stock</th>
                  <th style={{ padding: '8px 6px', textAlign: 'center', color: 'white', fontWeight: 600, fontSize: '0.7rem', width: '80px' }}>Quantity</th>
                  <th style={{ padding: '8px 6px', textAlign: 'right', color: 'white', fontWeight: 600, fontSize: '0.7rem', width: '80px' }}>Rate</th>
                  <th style={{ padding: '8px 6px', textAlign: 'center', color: 'white', fontWeight: 600, fontSize: '0.7rem', width: '70px' }}>GST%</th>
                  <th style={{ padding: '8px 6px', textAlign: 'right', color: 'white', fontWeight: 600, fontSize: '0.7rem', width: '100px' }}>Taxable</th>
                  <th style={{ padding: '8px 6px', textAlign: 'right', color: 'white', fontWeight: 600, fontSize: '0.7rem', width: '100px' }}>Total</th>
                  {!isViewMode && <th style={{ padding: '8px 6px', textAlign: 'center', color: 'white', fontWeight: 600, fontSize: '0.7rem', width: '50px' }}>Action</th>}
                </tr>
              </thead>
              <tbody>
                {transferDetails.map((d, index) => (
                  <tr key={index} style={{ background: index % 2 === 0 ? '#eff6ff' : '#ffffff', borderBottom: '1px solid #dbeafe' }}>
                    <td style={{ padding: '6px', textAlign: 'center', fontWeight: 500, color: '#2563eb', fontSize: '0.75rem' }}>{index + 1}</td>
                    <td style={{ padding: '6px 8px', textAlign: 'left', fontWeight: 500, color: '#1e3a8a' }}>
                      <span style={{ fontWeight: 600, color: '#1e3a8a', fontSize: '0.75rem' }}>{items.find(i => i.Id === parseInt(d.ItemId))?.Name || '-'}</span>
                    </td>
                    <td style={{ padding: '6px', textAlign: 'center', fontWeight: 600, color: '#6b7280', fontSize: '0.75rem' }}>
                      {d.HSNCode || '-'}
                    </td>
                    <td style={{ padding: '6px', textAlign: 'center', fontWeight: 600 }}>
                      {(() => {
                        const availableBalance = getAvailableBalance(d.ItemId, index);
                        const color = availableBalance <= 0 ? '#dc2626' : availableBalance <= 10 ? '#f59e0b' : '#059669';
                        return <span style={{ color, fontSize: '0.75rem' }}>{availableBalance}</span>;
                      })()}
                    </td>
                    <td style={{ padding: '6px', textAlign: 'center', fontWeight: 600, color: '#1e3a8a' }}>
                      {isViewMode ? (
                        <span style={{ fontWeight: 600, color: '#1e3a8a', fontSize: '0.75rem' }}>{d.Quantity || 0}</span>
                      ) : (
                        <TextField 
                          size="small" 
                          type="number" 
                          value={d.Quantity ?? ''} 
                          onChange={(e) => handleDetailChange(index, 'Quantity', e.target.value)} 
                          onFocus={(e) => e.target.select()} 
                          inputProps={{ 
                            min: 0, 
                            max: (() => {
                              return getAvailableBalance(d.ItemId, index);
                            })(),
                            style: { textAlign: 'center', padding: '3px', fontSize: '0.7rem' },
                            onKeyDown: (e) => {
                              if (e.key === 'Enter') {
                                e.target.blur();
                              }
                            }
                          }} 
                          sx={{ 
                            width: '70px', 
                            '& .MuiOutlinedInput-root': { height: '28px' },
                            '& input': {
                              transition: 'none'
                            }
                          }}
                          error={(() => {
                            const availableBalance = getAvailableBalance(d.ItemId, index);
                            return parseFloat(d.Quantity || 0) > availableBalance;
                          })()}
                        />
                      )}
                    </td>
                    <td style={{ padding: '6px', textAlign: 'right', fontWeight: 600, color: '#1e3a8a' }}>
                      {isViewMode ? (
                        <span style={{ fontWeight: 600, color: '#1e3a8a', fontSize: '0.75rem' }}>{formatCurrency(d.Rate)}</span>
                      ) : (
                        <TextField 
                          size="small" 
                          type="number" 
                          value={d.Rate ?? ''} 
                          onChange={(e) => handleDetailChange(index, 'Rate', e.target.value)} 
                          onFocus={(e) => e.target.select()} 
                          inputProps={{ 
                            min: 0, 
                            style: { textAlign: 'right', padding: '3px', fontSize: '0.7rem' },
                            onKeyDown: (e) => {
                              if (e.key === 'Enter') {
                                e.target.blur();
                              }
                            }
                          }} 
                          sx={{ 
                            width: '80px', 
                            '& .MuiOutlinedInput-root': { height: '28px' },
                            '& input': {
                              transition: 'none'
                            }
                          }} 
                        />
                      )}
                    </td>
                    <td style={{ padding: '6px', textAlign: 'center', fontWeight: 600, color: '#1e3a8a' }}>
                      {isViewMode ? (
                        <span style={{ fontWeight: 600, color: '#1e3a8a', fontSize: '0.75rem' }}>{d.GSTRate || 0}%</span>
                      ) : (
                        <TextField 
                          size="small" 
                          type="number" 
                          value={d.GSTRate ?? 0} 
                          onChange={(e) => handleDetailChange(index, 'GSTRate', e.target.value)} 
                          onFocus={(e) => e.target.select()} 
                          inputProps={{ 
                            min: 0, 
                            max: 100,
                            style: { textAlign: 'center', padding: '3px', fontSize: '0.7rem' },
                            onKeyDown: (e) => {
                              if (e.key === 'Enter') {
                                e.target.blur();
                              }
                            }
                          }} 
                          sx={{ 
                            width: '60px', 
                            '& .MuiOutlinedInput-root': { height: '28px' },
                            '& input': {
                              transition: 'none'
                            }
                          }} 
                        />
                      )}
                    </td>
                    <td style={{ padding: '6px', textAlign: 'right', fontWeight: 700, color: '#1e3a8a', fontSize: '0.75rem' }}>{formatCurrency(d.TaxableAmount)}</td>
                    <td style={{ padding: '6px', textAlign: 'right', fontWeight: 700, color: '#2563eb', fontSize: '0.75rem' }}>{formatCurrency(d.TotalAmount)}</td>
                    {!isViewMode && (
                      <td style={{ padding: '6px', textAlign: 'center' }}>
                        <IconButton 
                          size="small" 
                          onClick={() => removeDetailRow(index)} 
                          sx={{ p: 0.2, color: '#dc2626', '&:hover': { bgcolor: '#eff6ff' } }}
                        >
                          <Delete sx={{ fontSize: 14 }} />
                        </IconButton>
                      </td>
                    )}
                  </tr>
                ))}
                {transferDetails.length === 0 && (
                  <tr>
                    <td colSpan={isViewMode ? 9 : 10} style={{ padding: '16px', textAlign: 'center', color: '#6b7280', backgroundColor: '#eff6ff', fontSize: '0.8rem' }}>
                      No items added
                    </td>
                  </tr>
                )}
              </tbody>
              <tfoot style={{ position: 'sticky', bottom: 0, background: 'linear-gradient(135deg, #bfdbfe 0%, #93c5fd 100%)' }}>
                <tr style={{ borderTop: '2px solid #2563eb' }}>
                  <td colSpan="4" style={{ padding: '8px 6px', fontWeight: 700, textAlign: 'right', color: '#1e3a8a', fontSize: '0.75rem' }}>Totals:</td>
                  <td style={{ padding: '8px 6px', textAlign: 'center', fontWeight: 700, color: '#2563eb', fontSize: '0.75rem' }}>{transferDetails.reduce((sum, d) => sum + (parseFloat(d.Quantity) || 0), 0)}</td>
                  <td colSpan="2"></td>
                  <td style={{ padding: '8px 6px', textAlign: 'right', fontWeight: 700, color: '#2563eb', fontSize: '0.75rem' }}>{formatCurrency(transferDetails.reduce((sum, d) => sum + (parseFloat(d.TaxableAmount) || 0), 0))}</td>
                  <td style={{ padding: '8px 6px', textAlign: 'right', fontWeight: 700, color: '#2563eb', fontSize: '0.75rem' }}>{formatCurrency(transferDetails.reduce((sum, d) => sum + (parseFloat(d.TotalAmount) || 0), 0))}</td>
                  {!isViewMode && <td style={{ padding: '8px 6px' }}></td>}
                </tr>
              </tfoot>
            </table>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 1.5, py: 1, borderTop: '1px solid #dbeafe', bgcolor: '#eff6ff', justifyContent: 'flex-end' }}>
          <Button onClick={handleViewAllItemsClose} variant="outlined" size="small" sx={{ fontSize: '0.75rem', py: 0.4, px: 1.5 }}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Validation Error Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>

      {/* Cancel Confirmation Modal */}
      <Dialog
        open={cancelConfirmOpen}
        onClose={handleStayOnPage}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: '12px', padding: '8px' } }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Box sx={{
              backgroundColor: '#fef3c7',
              borderRadius: '50%',
              width: 40,
              height: 40,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              <Warning sx={{ color: '#f59e0b', fontSize: 24 }} />
            </Box>
            <Typography variant="h6" sx={{ fontWeight: 600, color: '#1e293b' }}>
              Unsaved Changes
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ color: '#64748b', fontSize: '0.9rem' }}>
            You have unsaved changes that will be lost if you leave this page.
          </Typography>
          <Typography sx={{ color: '#dc2626', fontSize: '0.85rem', mt: 1, fontWeight: 500 }}>
            Are you sure you want to exit without saving?
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
          <Button
            onClick={handleStayOnPage}
            variant="outlined"
            sx={{
              borderColor: '#dc2626',
              color: '#dc2626',
              '&:hover': { borderColor: '#b91c1c', backgroundColor: '#fef2f2' }
            }}
          >
            Stay on Page
          </Button>
          <Button
            onClick={handleConfirmCancel}
            variant="contained"
            sx={{ backgroundColor: '#dc2626', '&:hover': { backgroundColor: '#b91c1c' } }}
          >
            Exit Without Saving
          </Button>
        </DialogActions>
      </Dialog>

      {/* Get Items Modal */}
      <Dialog
        open={getItemsModalOpen}
        onClose={handleCloseGetItemsModal}
        maxWidth="xl"
        fullWidth
        fullScreen={isMobile}
        PaperProps={{ sx: { borderRadius: isMobile ? 0 : '12px', m: isMobile ? 0 : 2, maxHeight: '95vh' } }}
      >
        <DialogTitle sx={{ pb: 1, borderBottom: '1px solid #e2e8f0', bgcolor: '#f8fafc' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <List sx={{ color: '#1e40af', fontSize: 20 }} />
              <Typography variant="h6" sx={{ fontWeight: 600, color: '#1e293b', fontSize: '1.1rem' }}>
                Get Items ({filteredItems.length} available)
              </Typography>
            </Box>
            <IconButton onClick={handleCloseGetItemsModal} size="small">
              <Close fontSize="small" />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ p: 2, bgcolor: '#fafbfc' }}>
          {getItemsLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '300px' }}>
              <CircularProgress />
              <Typography variant="body2" sx={{ ml: 2, color: '#6b7280' }}>Loading items...</Typography>
            </Box>
          ) : (
            <>
              {/* Search */}
              <Box sx={{ mb: 2 }}>
                <TextField
                  fullWidth
                  size="small"
                  placeholder="Search items by name, HSN code, or item code..."
                  value={getItemsSearchValue}
                  onChange={(e) => { setGetItemsSearchValue(e.target.value); setCurrentItemsPage(1); }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Search sx={{ fontSize: 18, color: '#6b7280' }} />
                      </InputAdornment>
                    )
                  }}
                  sx={{
                    '& .MuiInputBase-input': { py: 1 },
                    '& .MuiOutlinedInput-root': {
                      bgcolor: '#fff',
                      '&:hover': { borderColor: '#1e40af' },
                      '&.Mui-focused': { borderColor: '#1e40af' }
                    }
                  }}
                />
              </Box>

              {/* Selected Items Summary */}
              {selectedItems.length > 0 && (
                <Box sx={{ mb: 2, p: 1.5, bgcolor: '#e3f2fd', borderRadius: 1, border: '1px solid #90caf9' }}>
                  <Typography variant="body2" sx={{ fontWeight: 600, color: '#1565c0', mb: 1, display: 'block' }}>
                    Selected Items ({selectedItems.length})
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {selectedItems.slice(0, 10).map(item => (
                      <Chip
                        key={item.Id}
                        label={`${item.Code ? item.Code + ' - ' : ''}${item.Name} (${item.selectedQuantity})`}
                        size="small"
                        onDelete={() => handleItemSelection(item, false)}
                        sx={{
                          fontSize: '0.75rem',
                          bgcolor: '#fff',
                          '& .MuiChip-deleteIcon': { color: '#1565c0' }
                        }}
                      />
                    ))}
                    {selectedItems.length > 10 && (
                      <Chip
                        label={`+${selectedItems.length - 10} more`}
                        size="small"
                        variant="outlined"
                        sx={{ fontSize: '0.75rem' }}
                      />
                    )}
                  </Box>
                </Box>
              )}

              {/* Items Table */}
              <Box sx={{
                border: '1px solid #e2e8f0',
                borderRadius: 2,
                overflow: 'hidden',
                bgcolor: '#fff',
                boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
              }}>
                <Box sx={{ maxHeight: 500, overflow: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
                    <thead style={{ position: 'sticky', top: 0, zIndex: 1 }}>
                      <tr style={{ background: 'linear-gradient(135deg, #1e40af 0%, #1d4ed8 100%)', height: '28px' }}>
                        <th style={{ padding: '2px 1px', textAlign: 'center', fontWeight: 600, color: '#fff', width: '4%', fontSize: '0.65rem', lineHeight: '1' }}>
                          <Checkbox
                            size="small"
                            checked={selectedItems.length === getPaginatedItems.length && getPaginatedItems.length > 0}
                            indeterminate={selectedItems.length > 0 && selectedItems.length < getPaginatedItems.length}
                            onChange={(e) => {
                              if (e.target.checked) {
                                getPaginatedItems.forEach(item => {
                                  if (!selectedItems.find(si => si.Id === item.Id) && !isItemAlreadyInTransfer(item.Id)) {
                                    handleItemSelection(item, true);
                                  }
                                });
                              } else {
                                getPaginatedItems.forEach(item => {
                                  handleItemSelection(item, false);
                                });
                              }
                            }}
                            sx={{ color: '#fff', '&.Mui-checked': { color: '#fff' }, transform: 'scale(0.8)' }}
                          />
                        </th>
                        <th style={{ padding: '2px 1px', textAlign: 'left', fontWeight: 600, color: '#fff', width: '32%', fontSize: '0.65rem', lineHeight: '1' }}>Item</th>
                        <th style={{ padding: '2px 1px', textAlign: 'center', fontWeight: 600, color: '#fff', width: '10%', fontSize: '0.65rem', lineHeight: '1' }}>HSN</th>
                        <th style={{ padding: '2px 1px', textAlign: 'center', fontWeight: 600, color: '#fff', width: '8%', fontSize: '0.65rem', lineHeight: '1' }}>GST%</th>
                        <th style={{ padding: '2px 1px', textAlign: 'center', fontWeight: 600, color: '#fff', width: '10%', fontSize: '0.65rem', lineHeight: '1' }}>Balance</th>
                        <th style={{ padding: '2px 1px', textAlign: 'center', fontWeight: 600, color: '#fff', width: '10%', fontSize: '0.65rem', lineHeight: '1' }}>Qty</th>
                        <th style={{ padding: '2px 1px', textAlign: 'center', fontWeight: 600, color: '#fff', width: '12%', fontSize: '0.65rem', lineHeight: '1' }}>Rate</th>
                        <th style={{ padding: '2px 1px', textAlign: 'center', fontWeight: 600, color: '#fff', width: '14%', fontSize: '0.65rem', lineHeight: '1' }}>Total</th>
                      </tr>
                    </thead>
                    <tbody>
                      {getPaginatedItems.map((item, index) => {
                        const selectedItem = selectedItems.find(si => si.Id === item.Id);
                        const isSelected = !!selectedItem;
                        const isAlreadyAdded = isItemAlreadyInTransfer(item.Id);
                        const availableBalance = getAvailableBalance(item.Id);

                        return (
                          <tr key={item.Id} style={{
                            borderBottom: '1px solid #f1f5f9',
                            background: isSelected ? '#e3f2fd' : (index % 2 === 0 ? '#fff' : '#f8fafc'),
                            opacity: isAlreadyAdded ? 0.6 : 1,
                            transition: 'background-color 0.2s ease',
                            cursor: isAlreadyAdded || availableBalance <= 0 ? 'not-allowed' : 'pointer'
                          }}
                          onClick={() => {
                            if (!isAlreadyAdded && availableBalance > 0) {
                              handleItemSelection(item, !isSelected);
                            }
                          }}
                          >
                            <td style={{ padding: '4px', textAlign: 'center' }} onClick={(e) => e.stopPropagation()}>
                              <Checkbox
                                size="small"
                                checked={isSelected}
                                disabled={isAlreadyAdded || availableBalance <= 0}
                                onChange={(e) => handleItemSelection(item, e.target.checked)}
                                sx={{
                                  color: isSelected ? '#1e40af' : '#9ca3af',
                                  '&.Mui-checked': { color: '#1e40af' }
                                }}
                              />
                            </td>
                            <td style={{ padding: '4px 6px', fontWeight: 500, color: '#1e293b' }}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <span style={{ fontSize: '0.8rem' }}>{item.Code ? `${item.Code} - ${item.Name}` : item.Name}</span>
                                {isAlreadyAdded && (
                                  <Chip
                                    label="Added"
                                    size="small"
                                    sx={{
                                      bgcolor: '#dcfce7',
                                      color: '#166534',
                                      fontSize: '0.6rem',
                                      height: 16,
                                      '& .MuiChip-label': { px: 0.5 }
                                    }}
                                  />
                                )}
                                {/* {!isAlreadyAdded && availableBalance <= 0 && (
                                  <Chip
                                    label="Out of Stock"
                                    size="small"
                                    sx={{
                                      bgcolor: '#fecaca',
                                      color: '#dc2626',
                                      fontSize: '0.6rem',
                                      height: 16,
                                      '& .MuiChip-label': { px: 0.5 }
                                    }}
                                  />
                                )} */}
                              </Box>
                            </td>
                            <td style={{ padding: '4px', textAlign: 'center', color: '#6b7280', fontSize: '0.75rem' }}>
                              {item.HSNCode || '-'}
                            </td>
                            <td style={{ padding: '4px', textAlign: 'center', fontWeight: 500, color: '#1e40af', fontSize: '0.75rem' }}>
                              {item.GSTRate || 0}%
                            </td>
                            <td style={{ padding: '4px', textAlign: 'center', fontWeight: 600, fontSize: '0.75rem', color: availableBalance <= 0 ? '#dc2626' : availableBalance <= 10 ? '#f59e0b' : '#059669' }}>
                              {item.CurrentBranchStock}
                            </td>
                            <td style={{ padding: '4px' }} onClick={(e) => e.stopPropagation()}>
                              {isSelected ? (
                                <TextField
                                  size="small"
                                  type="number"
                                  value={selectedItem.selectedQuantity}
                                  onChange={(e) => handleSelectedItemChange(item.Id, 'selectedQuantity', e.target.value)}
                                  inputProps={{
                                    min: 1,
                                    max: availableBalance,
                                    style: { textAlign: 'right', direction: 'ltr' }
                                  }}
                                  sx={{
                                    width: 70,
                                    '& input': { textAlign: 'right !important', direction: 'ltr !important' },
                                    '& .MuiInputBase-input': { py: 0.25, px: 0.5, fontSize: '0.75rem', bgcolor: '#fff', textAlign: 'right !important' },
                                    '& .MuiOutlinedInput-root': { '&:hover': { borderColor: '#1e40af' }, '&.Mui-focused': { borderColor: '#1e40af' } }
                                  }}
                                  error={selectedItem.selectedQuantity > availableBalance}
                                />
                              ) : (
                                <span style={{ display: 'block', textAlign: 'center', color: '#9ca3af', fontSize: '0.75rem' }}>1</span>
                              )}
                            </td>
                            <td style={{ padding: '4px' }} onClick={(e) => e.stopPropagation()}>
                              {isSelected ? (
                                <TextField
                                  size="small"
                                  type="number"
                                  value={selectedItem.selectedRate}
                                  onChange={(e) => handleSelectedItemChange(item.Id, 'selectedRate', e.target.value)}
                                  inputProps={{
                                    min: 0,
                                    style: { textAlign: 'right', direction: 'ltr' }
                                  }}
                                  sx={{
                                    width: 90,
                                    '& input': { textAlign: 'right !important', direction: 'ltr !important' },
                                    '& .MuiInputBase-input': { py: 0.25, px: 0.5, fontSize: '0.75rem', bgcolor: '#fff', textAlign: 'right !important' },
                                    '& .MuiOutlinedInput-root': { '&:hover': { borderColor: '#1e40af' }, '&.Mui-focused': { borderColor: '#1e40af' } }
                                  }}
                                />
                              ) : (
                                <span style={{ display: 'block', textAlign: 'center', color: '#6b7280', fontSize: '0.75rem' }}>
                                  {formatCurrency(item.PurchaseRate || item.SaleRate || item.Rate || 0)}
                                </span>
                              )}
                            </td>
                            <td style={{ padding: '4px', textAlign: 'center', fontWeight: 600, color: '#1e40af', fontSize: '0.75rem' }}>
                              {isSelected ? (
                                (() => {
                                  const qty = parseFloat(selectedItem.selectedQuantity) || 0;
                                  const rate = parseFloat(selectedItem.selectedRate) || 0;
                                  const gstRate = parseFloat(item.GSTRate) || 0;
                                  const amount = qty * rate;
                                  const gstAmount = (amount * gstRate) / 100;
                                  return formatCurrency(amount + gstAmount);
                                })()
                              ) : (
                                formatCurrency(item.PurchaseRate || item.SaleRate || item.Rate || 0)
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </Box>
              </Box>

              {/* Pagination */}
              {totalItemsPages > 1 && (
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                  <Pagination
                    count={totalItemsPages}
                    page={currentItemsPage}
                    onChange={(e, page) => setCurrentItemsPage(page)}
                    size="small"
                    color="primary"
                  />
                </Box>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 2, py: 1.5, borderTop: '1px solid #e2e8f0', bgcolor: '#f8fafc' }}>
          <Button onClick={handleCloseGetItemsModal} variant="outlined" size="small">
            Cancel
          </Button>
          <Button
            onClick={handleAddSelectedItems}
            variant="contained"
            disabled={selectedItems.length === 0 || addingItemsLoading}
            size="small"
            sx={{ bgcolor: '#1e40af', '&:hover': { bgcolor: '#1d4ed8' } }}
          >
            {addingItemsLoading ? (
              <>
                <CircularProgress size={16} sx={{ mr: 1, color: '#fff' }} />
                Adding...
              </>
            ) : (
              `+ Add ${selectedItems.length} Items`
            )}
          </Button>
        </DialogActions>
      </Dialog>
    </Dialog>
  );
};

export default StockTransferOutModal;


